/**
 * ScopingQuestionnaire Component
 * 
 * Presents altitude-determining questions during item shaping.
 * Questions are contextually adapted based on the item's candidate_type
 * (Problem, Opportunity, Solution, Risk, Investment, Commitment).
 * 
 * The three dimensions (Time Horizon, Work Size, Impact Surface) determine
 * the altitude level (1-5) which is used for leveling and comparability.
 */

import { useEffect, useMemo, useState } from 'react';
import { Clock, Layers, Target, Pencil } from 'lucide-react';
import type { AltitudeScopingInputs, TimeHorizon, WorkSize, ImpactSurface } from '../../../types/prioritization/leveling';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import { ALTITUDE_LEVEL_NAMES, ALTITUDE_LEVEL_DESCRIPTIONS, ALTITUDE_LEVELS_ORDERED } from '../../../types/prioritization/itemLifecycle';
import type { ItemLanguageType } from '../../../types/prioritization/lifecycle';
import { computeAltitudeLevel } from '../../../lib/altitudePrediction';
import { 
  getAltitudeQuestionSet,
  getAltitudeQuestionSetFromDB,
  type AltitudeQuestion, 
  type AltitudeQuestionSet,
} from '../../../lib/altitudeQuestions';
import { useItemCategoriesStore } from '../../../stores/prioritization/itemCategoriesStore';
import {
  TIME_HORIZON_OPTIONS,
  WORK_SIZE_OPTIONS,
  IMPACT_SURFACE_OPTIONS,
} from '../../../types/prioritization/leveling';

// ============================================================================
// Props
// ============================================================================

export interface ScopingQuestionnaireProps {
  value: AltitudeScopingInputs;
  onChange: (inputs: AltitudeScopingInputs) => void;
  onAltitudeComputed?: (level: AltitudeLevel | undefined) => void;
  candidateType?: ItemLanguageType;
  compact?: boolean;
  disabled?: boolean;
  /**
   * When provided, option buttons that match these AI-suggested values render with
   * a violet/purple accent instead of the standard primary color — indicating the
   * answer was originally provided by the ATC agent, not the user.
   * Clicking any option clears the AI accent for that dimension automatically.
   */
  aiSetScopingInputs?: AltitudeScopingInputs;
  /**
   * When provided (e.g. a level the user previously set directly), the component
   * will open in "direct" mode with that level pre-selected.
   */
  directAltitudeValue?: AltitudeLevel;
  /**
   * Called when the user picks an altitude level directly instead of via quiz.
   */
  onDirectAltitude?: (level: AltitudeLevel) => void;
}

// ============================================================================
// Dimension Icons
// ============================================================================

const DIMENSION_ICONS: Record<string, typeof Clock> = {
  timeHorizon: Clock,
  workSize: Layers,
  impactSurface: Target,
};

// Module-level constant so useMemo dependency arrays don't get a new Set each render
const BUILTIN_SLUGS = new Set(['problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment']);

// ============================================================================
// Component
// ============================================================================

export function ScopingQuestionnaire({
  value,
  onChange,
  onAltitudeComputed,
  candidateType,
  compact = false,
  disabled = false,
  aiSetScopingInputs,
  directAltitudeValue,
  onDirectAltitude,
}: ScopingQuestionnaireProps) {
  // Track which dimensions the user has manually overridden so we can drop the AI accent
  const [userOverriddenDimensions, setUserOverriddenDimensions] = useState<Set<string>>(new Set());

  // "direct" mode lets the user pick 1-5 without answering the quiz.
  // Default to direct if a directAltitudeValue is provided but scoping inputs are incomplete.
  const hasIncompleteQuiz = !value.timeHorizon || !value.workSize || !value.impactSurface;
  const [mode, setMode] = useState<'quiz' | 'direct'>(
    directAltitudeValue != null && hasIncompleteQuiz ? 'direct' : 'quiz'
  );

  // Local state for the directly-chosen level (synced from prop when ATC/user sets altitude externally)
  const [selectedDirectLevel, setSelectedDirectLevel] = useState<AltitudeLevel | undefined>(directAltitudeValue);

  useEffect(() => {
    if (directAltitudeValue != null) {
      setSelectedDirectLevel(directAltitudeValue);
    }
  }, [directAltitudeValue]);

  const computedFromQuiz = computeAltitudeLevel(value);
  const activeLevel: AltitudeLevel | undefined =
    directAltitudeValue ?? selectedDirectLevel ?? computedFromQuiz;

  const scopingMatchesAi = !!(
    aiSetScopingInputs?.timeHorizon &&
    aiSetScopingInputs?.workSize &&
    aiSetScopingInputs?.impactSurface &&
    value.timeHorizon === aiSetScopingInputs.timeHorizon &&
    value.workSize === aiSetScopingInputs.workSize &&
    value.impactSurface === aiSetScopingInputs.impactSurface &&
    userOverriddenDimensions.size === 0
  );
  const isAiSetLevel = scopingMatchesAi && activeLevel === computeAltitudeLevel(aiSetScopingInputs);
  
  // For builtins, use the static question set; for custom categories, load from DB
  const staticQuestionSet = useMemo(() => {
    if (candidateType && BUILTIN_SLUGS.has(candidateType)) {
      return getAltitudeQuestionSet(candidateType);
    }
    return null;
  }, [candidateType]);

  const [dbQuestionSet, setDbQuestionSet] = useState<AltitudeQuestionSet | null>(null);
  const categories = useItemCategoriesStore((s) => s.categories);

  useEffect(() => {
    if (!candidateType || BUILTIN_SLUGS.has(candidateType)) {
      setDbQuestionSet(null);
      return;
    }
    // Custom category: find its ID and load questions from DB
    const cat = categories.find((c) => c.slug === candidateType);
    if (!cat) return;
    getAltitudeQuestionSetFromDB(cat.slug, cat.id, cat.label, cat.description ?? '').then((qs) => {
      setDbQuestionSet(qs);
    });
  }, [candidateType, categories]);

  const questionSet = staticQuestionSet ?? dbQuestionSet;
  
  // Compute altitude whenever inputs change
  useEffect(() => {
    const level = computeAltitudeLevel(value);
    onAltitudeComputed?.(level);
  }, [value, onAltitudeComputed]);
  
  // Handle selection for a dimension
  const handleSelect = (
    dimension: 'timeHorizon' | 'workSize' | 'impactSurface',
    selectedValue: TimeHorizon | WorkSize | ImpactSurface
  ) => {
    if (disabled) return;
    // Mark this dimension as user-overridden so the AI accent is dropped
    setUserOverriddenDimensions((prev) => {
      const next = new Set(prev);
      next.add(dimension);
      return next;
    });
    onChange({
      ...value,
      [dimension]: selectedValue,
    });
  };
  
  // Render a single question section
  const renderQuestion = (
    dimension: 'timeHorizon' | 'workSize' | 'impactSurface',
    question: AltitudeQuestion | null,
    genericOptions: { value: string; label: string; description: string }[],
    currentValue: string | undefined
  ) => {
    const Icon = DIMENSION_ICONS[dimension];
    
    // Use contextual question if available, otherwise generic
    const questionText = question?.question ?? getGenericQuestion(dimension);
    const helpText = question?.helpText;
    const options = question?.options ?? genericOptions.map((opt, idx) => ({
      value: (idx + 1) as 1 | 2 | 3 | 4 | 5,
      label: opt.label,
      description: opt.description,
      mappedValue: opt.value,
    }));

    // AI-set value for this dimension (cleared once user manually picks)
    const aiValue = !userOverriddenDimensions.has(dimension)
      ? aiSetScopingInputs?.[dimension]
      : undefined;
    
    return (
      <div className={compact ? 'mb-5' : 'mb-6'}>
        <div className="flex items-start gap-2 mb-2.5">
          <Icon className="h-4 w-4 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
          <div>
            <p className={`font-medium text-[var(--text-pri)] ${compact ? 'text-sm' : 'text-base'}`}>
              {questionText}
            </p>
            {helpText && (
              <p className="text-xs text-[var(--text-ter)] mt-0.5">{helpText}</p>
            )}
          </div>
        </div>
        
        <div className={`grid gap-1.5 ${compact ? 'grid-cols-5' : 'grid-cols-1 sm:grid-cols-5'}`}>
          {options.map((option) => {
            const isSelected = currentValue === option.mappedValue;
            const isAiSet = isSelected && aiValue === option.mappedValue;
            return (
              <button
                key={option.value}
                type="button"
                disabled={disabled}
                onClick={() => handleSelect(dimension, option.mappedValue as TimeHorizon | WorkSize | ImpactSurface)}
                title={isAiSet ? 'Set by ATC agent — click to override' : undefined}
                className={`
                  flex flex-col gap-1 p-2.5  border text-left transition-all
                  ${isAiSet
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-violet-400'
                    : isSelected
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50/40'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {/* Number badge */}
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold flex-shrink-0 ${
                  isAiSet
                    ? 'bg-[var(--slate)] text-white'
                    : isSelected
                      ? 'bg-primary-600 text-white'
                      : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                }`}>
                  {option.value}
                </span>
                {/* Label */}
                <span className={`text-xs font-semibold leading-tight ${
                  isAiSet ? 'text-[var(--text-ter)]' : isSelected ? 'text-primary-800' : 'text-[var(--text-pri)]'
                }`}>
                  {option.label}
                </span>
                {/* Description — always visible */}
                <span className="text-[10px] leading-tight text-[var(--text-ter)]">
                  {option.description}
                </span>
              </button>
            );
          })}
        </div>
        {aiValue && currentValue === aiValue && !userOverriddenDimensions.has(dimension) && (
          <p className="mt-1 text-[10px] text-[var(--text-ter)] flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--slate)]" />
            ATC agent suggestion — click another option to override
          </p>
        )}
      </div>
    );
  };
  
  // Get the contextual question for a dimension, or null
  const getContextualQuestion = (dimension: 'timeHorizon' | 'workSize' | 'impactSurface'): AltitudeQuestion | null => {
    if (!questionSet) return null;
    return questionSet.questions.find(q => q.dimension === dimension) ?? null;
  };
  
  const handleDirectSelect = (level: AltitudeLevel) => {
    if (disabled) return;
    setSelectedDirectLevel(level);
    onAltitudeComputed?.(level);
    onDirectAltitude?.(level);
  };

  const renderAltitudeLevelStrip = (interactive: boolean) => (
    <div className={`grid gap-2 ${compact ? 'grid-cols-5' : 'grid-cols-5'} ${interactive ? 'mb-1' : 'mb-4'}`}>
      {ALTITUDE_LEVELS_ORDERED.map((level) => {
        const isSelected = activeLevel === level;
        const isAiLevel = isSelected && isAiSetLevel;
        const Tag = interactive ? 'button' : 'div';
        return (
          <Tag
            key={level}
            {...(interactive
              ? {
                  type: 'button' as const,
                  disabled,
                  onClick: () => handleDirectSelect(level),
                }
              : {})}
            title={ALTITUDE_LEVEL_DESCRIPTIONS[level]}
            className={`
              flex flex-col gap-1 p-2.5  border text-left transition-all
              ${isAiLevel
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] ring-1 ring-violet-400'
                : isSelected
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : interactive
                    ? 'border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50/40'
                    : 'border-[var(--rule)] bg-[var(--bg-raised)]'
              }
              ${interactive && !disabled ? 'cursor-pointer' : ''}
              ${interactive && disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold flex-shrink-0 ${
              isAiLevel
                ? 'bg-[var(--slate)] text-white'
                : isSelected
                  ? 'bg-primary-600 text-white'
                  : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
            }`}>
              {level}
            </span>
            <span className={`text-xs font-semibold leading-tight ${
              isAiLevel ? 'text-[var(--text-ter)]' : isSelected ? 'text-primary-800' : 'text-[var(--text-pri)]'
            }`}>
              {ALTITUDE_LEVEL_NAMES[level]}
            </span>
            <span className={`text-[10px] leading-tight ${isSelected ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              {ALTITUDE_LEVEL_DESCRIPTIONS[level]}
            </span>
          </Tag>
        );
      })}
    </div>
  );

  return (
    <div className={disabled ? 'opacity-60' : ''}>
      {/* Mode toggle */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[var(--text-ter)]">
          {mode === 'quiz' ? 'Answer 3 questions to compute altitude' : 'Altitude set directly'}
        </span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            const next = mode === 'quiz' ? 'direct' : 'quiz';
            setMode(next);
            if (next === 'quiz') {
              // Switching back to quiz — clear the direct selection and re-emit computed value
              setSelectedDirectLevel(undefined);
              const computed = computeAltitudeLevel(value);
              onAltitudeComputed?.(computed);
            }
          }}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-40"
        >
          <Pencil className="h-3 w-3" />
          {mode === 'quiz' ? 'Set number directly' : 'Use quiz instead'}
        </button>
      </div>

      {mode === 'direct' ? (
        renderAltitudeLevelStrip(true)
      ) : (
        /* ── Quiz mode ── */
        <>
          {activeLevel != null && (
            <div className="mb-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)] mb-2">
                Computed altitude
              </p>
              {renderAltitudeLevelStrip(false)}
              {isAiSetLevel && (
                <p className="mt-1 text-[10px] text-[var(--text-ter)] flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--slate)]" />
                  ATC agent suggestion — adjust scoping answers below or switch to direct selection
                </p>
              )}
            </div>
          )}

          {candidateType && questionSet && (
            <div className="mb-4 px-3 py-2 bg-[var(--slate-dim)] border border-[var(--slate)]">
              <p className="text-xs text-[var(--text-sec)]">
                <span className="font-medium">Questions adapted for:</span>{' '}
                {questionSet.displayName} — {questionSet.description}
              </p>
            </div>
          )}

          {renderQuestion(
            'timeHorizon',
            getContextualQuestion('timeHorizon'),
            TIME_HORIZON_OPTIONS,
            value.timeHorizon
          )}

          {renderQuestion(
            'workSize',
            getContextualQuestion('workSize'),
            WORK_SIZE_OPTIONS,
            value.workSize
          )}

          {renderQuestion(
            'impactSurface',
            getContextualQuestion('impactSurface'),
            IMPACT_SURFACE_OPTIONS,
            value.impactSurface
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Generic Questions (fallback when no candidate type is selected)
// ============================================================================

function getGenericQuestion(dimension: 'timeHorizon' | 'workSize' | 'impactSurface'): string {
  switch (dimension) {
    case 'timeHorizon':
      return 'When would we see meaningful impact?';
    case 'workSize':
      return 'How much effort is involved?';
    case 'impactSurface':
      return 'What is the breadth of effect?';
  }
}
